/**
 * ExternalRelease store using Zustand.
 */

import { create } from 'zustand';

import {
  listExternalReleases,
  getExternalRelease,
  createExternalRelease,
  updateExternalRelease,
  deleteExternalRelease,
  getExternalReleaseLogs,
} from '@/api/externalRelease';
import type {
  ExternalRelease,
  ExternalReleaseDetail,
  ExternalReleaseCreate,
  ExternalReleaseUpdate,
  ExternalReleaseQueryParams,
  OperationLog,
} from '@/types';

interface ExternalReleaseState {
  releases: ExternalRelease[];
  currentRelease: ExternalReleaseDetail | null;
  logs: OperationLog[];
  pagination: {
    count: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchReleases: (params?: ExternalReleaseQueryParams) => Promise<void>;
  fetchRelease: (id: number) => Promise<void>;
  createRelease: (data: ExternalReleaseCreate) => Promise<ExternalReleaseDetail>;
  updateRelease: (id: number, data: ExternalReleaseUpdate) => Promise<ExternalReleaseDetail>;
  deleteRelease: (id: number) => Promise<void>;
  fetchLogs: (id: number) => Promise<void>;
  setReleaseUploadStatus: (id: number, status: ExternalRelease['upload_status']) => void;
  clearCurrentRelease: () => void;
  clearError: () => void;
}

export const useExternalReleaseStore = create<ExternalReleaseState>()((set) => ({
  releases: [],
  currentRelease: null,
  logs: [],
  pagination: {
    count: 0,
    page: 1,
    pageSize: 20,
    totalPages: 0,
  },
  isLoading: false,
  error: null,

  fetchReleases: async (params?: ExternalReleaseQueryParams) => {
    set({ isLoading: true, error: null });
    try {
      const response = await listExternalReleases(params);
      set({
        releases: response.results,
        pagination: {
          count: response.count,
          page: response.page,
          pageSize: response.page_size,
          totalPages: response.total_pages,
        },
        isLoading: false,
      });
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        '获取数据失败';
      set({ error: message, isLoading: false });
    }
  },

  fetchRelease: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      const release = await getExternalRelease(id);
      set({ currentRelease: release, isLoading: false });
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        '获取详情失败';
      set({ error: message, isLoading: false });
    }
  },

  createRelease: async (data: ExternalReleaseCreate) => {
    set({ isLoading: true, error: null });
    try {
      const release = await createExternalRelease(data);
      set({ isLoading: false });
      return release;
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        '创建失败';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updateRelease: async (id: number, data: ExternalReleaseUpdate) => {
    set({ isLoading: true, error: null });
    try {
      const release = await updateExternalRelease(id, data);
      set({ currentRelease: release, isLoading: false });
      return release;
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        '更新失败';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  deleteRelease: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      await deleteExternalRelease(id);
      set((state) => ({
        releases: state.releases.filter((r) => r.id !== id),
        isLoading: false,
      }));
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        '删除失败';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  fetchLogs: async (id: number) => {
    try {
      const response = await getExternalReleaseLogs(id);
      set({ logs: response.results });
    } catch {
      set({ logs: [] });
    }
  },

  setReleaseUploadStatus: (id, status) =>
    set((state) => ({
      releases: state.releases.map((r) => r.id === id ? { ...r, upload_status: status } : r),
    })),

  clearCurrentRelease: () => set({ currentRelease: null, logs: [] }),

  clearError: () => set({ error: null }),
}));
